import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { QcResponseGrid } from 'src/app/shared/models/grid.model';
import { IS_ACTION, IS_LOAD, IS_SEARCH } from 'src/app/shared/models/load.models';
import { QcResponse } from 'src/app/shared/models/qcResponse.model';
import { EnvironmentFacadeService } from 'src/app/shared/services/environment.facade.service';
import { PaginatorLiteralsFacadeService } from 'src/app/shared/services/literals/paginator.literals.facade.service';
import { TicketsLiteralsFacadeService } from 'src/app/shared/services/literals/tickets.literals.facade.service';
import { PaginatorFacadeService } from 'src/app/shared/services/paginator.facade.service';
import {
  Assignment,
  Category,
  Country,
  Member,
  Schema,
  Severity,
  Status,
  TicketResponse,
} from '../models/tickets.models';
import { EditTicketFacadeService } from './edit-ticket.facade.service';
import { TicketsSearchFacadeService } from './tickets-search.facade.service';

@Injectable({ providedIn: 'root' })
export class TicketsSearchService {
  constructor(
    private http: HttpClient,
    private environmentFacadeService: EnvironmentFacadeService,
    private paginatorLiterals: PaginatorLiteralsFacadeService,
    private ticketsLiterals: TicketsLiteralsFacadeService,
    private paginatorService: PaginatorFacadeService,
    private ticketsSearchFacade: TicketsSearchFacadeService,
    private editTicketFacade: EditTicketFacadeService,
  ) {}

  severity(): Observable<Severity[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/ticket-properties/severities';

    return this.http.get<Severity[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  schemas(): Observable<Schema[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/loyalty/schemas';

    return this.http.get<Schema[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  status(): Observable<Status[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/ticket-properties/statuses';

    return this.http.get<Status[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  countries(): Observable<Country[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/countries';

    return this.http.get<Country[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  categories(): Observable<Category[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/ticket-properties/categories';

    return this.http.get<Category[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  assignees(): Observable<Assignment[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/ticket-properties/assignments';

    return this.http.get<Assignment[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  ticketsCount(): Observable<number> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/tickets/count';

    const ticketSeverityId =
      this.ticketsSearchFacade.ticketSeverityValues.filter(
        (severity: Severity) => severity.description === this.ticketsSearchFacade.ticketSeverity,
      )[0]?.id ?? null;

    const loyaltySchemaId =
      this.ticketsSearchFacade.loyaltySchemaValues.filter(
        (schema: Schema) => schema.loyaltySchemaName === this.ticketsSearchFacade.loyaltySchema,
      )[0]?.id ?? null;

    const ticketStatusId =
      this.ticketsSearchFacade.statusValues.filter(
        (status: Status) => status.description === this.ticketsSearchFacade.status,
      )[0]?.id ?? null;

    const countryId =
      this.ticketsSearchFacade.countryValues.filter(
        (country: Country) => country.country === this.ticketsSearchFacade.country,
      )[0]?.id ?? null;

    const ticketAssigneeId =
      this.ticketsSearchFacade.assigneeValues.filter(
        (assignee: Assignment) => assignee.description === this.ticketsSearchFacade.assignee,
      )[0]?.id ?? null;

    const ticketCategoryId =
      this.ticketsSearchFacade.categoryValues.filter(
        (category: Category) => category.description === this.ticketsSearchFacade.category,
      )[0]?.id ?? null;

    const body = {
      ticketId: this.ticketsSearchFacade.ticketID,
      ticketSeverityId: ticketSeverityId,
      loyaltySchemaId: loyaltySchemaId,
      ticketStatusId: ticketStatusId,
      countryId: countryId,
      ticketCategoryId: ticketCategoryId,
      ticketAssigneeId: ticketAssigneeId,
      withoutAssignee: ticketAssigneeId === -1 ? true : false,
      withoutSeverity: ticketSeverityId === -1 ? true : false,
      body: this.ticketsSearchFacade.body,
      subject: this.ticketsSearchFacade.subject,
      first: this.paginatorService.pageIndex * 10,
      pageSize: this.paginatorService.itemsNumber,
      sortField: this.getSortField(this.paginatorService.selectedSortField),
      sortOrder: this.paginatorService.selectedOrder === this.paginatorLiterals.orderAsc ? 'ASC' : 'DESC',
    };

    return this.http.post<number>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  QC_API_CREATE_TICKET(): Observable<QcResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flowevent/QC_API_CREATE_TICKET';

    return this.http.get<QcResponse>(url, {
      withCredentials: true,
    });
  }

  QC_API_UPDATE_TICKET(): Observable<QcResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flowevent/QC_API_UPDATE_TICKET';

    return this.http.get<QcResponse>(url, {
      withCredentials: true,
    });
  }

  ticketsSearch(): Observable<QcResponseGrid> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/tickets/findByFilter';

    const ticketSeverityId =
      this.ticketsSearchFacade.ticketSeverityValues.filter(
        (severity: Severity) => severity.description === this.ticketsSearchFacade.ticketSeverity,
      )[0]?.id ?? null;

    const loyaltySchemaId =
      this.ticketsSearchFacade.loyaltySchemaValues.filter(
        (schema: Schema) => schema.loyaltySchemaName === this.ticketsSearchFacade.loyaltySchema,
      )[0]?.id ?? null;

    const ticketStatusId =
      this.ticketsSearchFacade.statusValues.filter(
        (status: Status) => status.description === this.ticketsSearchFacade.status,
      )[0]?.id ?? null;

    const countryId =
      this.ticketsSearchFacade.countryValues.filter(
        (country: Country) => country.country === this.ticketsSearchFacade.country,
      )[0]?.id ?? null;

    const ticketAssigneeId =
      this.ticketsSearchFacade.assigneeValues.filter(
        (assignee: Assignment) => assignee.description === this.ticketsSearchFacade.assignee,
      )[0]?.id ?? null;

    const ticketCategoryId =
      this.ticketsSearchFacade.categoryValues.filter(
        (category: Category) => category.description === this.ticketsSearchFacade.category,
      )[0]?.id ?? null;

    const body = {
      ticketId: this.ticketsSearchFacade.ticketID,
      ticketSeverityId: ticketSeverityId,
      loyaltySchemaId: loyaltySchemaId,
      ticketStatusId: ticketStatusId,
      countryId: countryId,
      ticketCategoryId: ticketCategoryId,
      ticketAssigneeId: ticketAssigneeId,
      withoutAssignee: ticketAssigneeId === -1 ? true : false,
      withoutSeverity: ticketSeverityId === -1 ? true : false,
      body: this.ticketsSearchFacade.body,
      subject: this.ticketsSearchFacade.subject,
      first: this.paginatorService.pageIndex * this.paginatorService.itemsNumber,
      pageSize: this.paginatorService.itemsNumber,
      sortField: this.getSortField(this.paginatorService.selectedSortField),
      sortOrder: this.paginatorService.selectedOrder === this.paginatorLiterals.orderAsc ? 'ASC' : 'DESC',
    };

    return this.http.post<QcResponseGrid>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  getSortField(sortOption: string) {
    const sortOptions: { [option: string]: string } = {
      [this.ticketsLiterals.sortId]: 'id',
      [this.ticketsLiterals.sortInitiationDate]: 'initiationDate',
      [this.ticketsLiterals.sortTableTicketStatus]: 'tableTicketStatus',
      [this.ticketsLiterals.sortInitiator]: 'initiator',
      [this.ticketsLiterals.ticketAssignee]: 'tableTicketAssignee',
      [this.ticketsLiterals.ticketSeverity]: 'tableTicketSeverity',
      [this.ticketsLiterals.ticketCategory]: 'tableTicketCategory',
      [this.ticketsLiterals.sortSubject]: 'subject',
      [this.ticketsLiterals.sortCustomer]: 'customer',
      [this.ticketsLiterals.sortLoyaltySchema]: 'loyaltySchema',
    };

    return sortOptions[sortOption];
  }

  getMember(): Observable<Member[]> {
    const url =
      this.environmentFacadeService.getEnvironment() +
      '/rest/v1/loyalty/members/ticket-member?uid=' +
      this.editTicketFacade.searchField;

    return this.http.get<Member[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  createTicket(): Observable<TicketResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/tickets/';

    const body = {
      ticketId: this.editTicketFacade.ticket.length ? this.editTicketFacade.ticket[0]?.ticketId : '',
      ticketDate: this.editTicketFacade.ticket.length ? this.editTicketFacade.ticket[0]?.ticketDate : '',
      ticketStatusId: this.editTicketFacade.status === 0 ? '' : this.editTicketFacade.status,
      ticketCategoryId: this.editTicketFacade.category,
      ticketAssigneeId: this.editTicketFacade.assignee === 0 ? '' : this.editTicketFacade.assignee,
      ticketSeverityId: this.editTicketFacade.ticketSeverity === 0 ? '' : this.editTicketFacade.ticketSeverity,
      appUser: this.editTicketFacade.ticket.length ? this.editTicketFacade.ticket[0]?.appUser : '',
      subject: this.editTicketFacade.subject,
      body: this.editTicketFacade.body,
      membersSchemaId: this.editTicketFacade.member.length
        ? (this.editTicketFacade.member[0]?.ticketLoyaltyMemberDto?.loyaltySchemaId ?? '')
        : '',
      countryId: this.editTicketFacade.country,
      ticketCustomer: this.editTicketFacade.member.length ? this.editTicketFacade.member[0] : null,
      executeFlow: false,
    };

    return this.http.put<TicketResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  updateTicket(): Observable<TicketResponse> {
    const url =
      this.environmentFacadeService.getEnvironment() + '/rest/v1/tickets/' + this.editTicketFacade.ticket[0].ticketId;

    const body = {
      ticketId: this.editTicketFacade.ticket.length ? this.editTicketFacade.ticket[0]?.ticketId : '',
      ticketDate: this.editTicketFacade.ticket.length ? this.editTicketFacade.ticket[0]?.ticketDate : '',
      ticketStatusId: this.editTicketFacade.status === 0 ? '' : this.editTicketFacade.status,
      ticketCategoryId: this.editTicketFacade.category,
      ticketAssigneeId: this.editTicketFacade.assignee === 0 ? '' : this.editTicketFacade.assignee,
      ticketSeverityId: this.editTicketFacade.ticketSeverity === 0 ? '' : this.editTicketFacade.ticketSeverity,
      appUser: this.editTicketFacade.ticket.length ? this.editTicketFacade.ticket[0]?.appUser : '',
      subject: this.editTicketFacade.subject,
      body: this.editTicketFacade.body,
      membersSchemaId: this.editTicketFacade.member.length
        ? (this.editTicketFacade.member[0]?.ticketLoyaltyMemberDto?.loyaltySchemaId ?? '')
        : '',
      countryId: this.editTicketFacade.country,
      ticketCustomer: this.editTicketFacade.member.length ? this.editTicketFacade.member[0] : null,
      executeFlow: false,
      changeDate: this.editTicketFacade.ticket[0].changeDate,
      changedBy: this.editTicketFacade.ticket[0].changedBy,
    };

    return this.http.put<TicketResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }
}
